"use client";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";

const rawData = [
  {
    country: "Indonesia",
    flag: "🇮🇩",
    tfec: 136256,
    tfecWidth: 136.25,
    industry: 58.46,
    smes: 23.35,
    total: 176.9,
  },
  {
    country: "Thailand",
    flag: "🇹🇭",
    tfec: 208338,
    tfecWidth: 208.33,
    industry: 34.62,
    smes: 10.45,
    total: 73.77,
  },
  {
    country: "Vietnam",
    flag: "🇻🇳",
    tfec: 103303,
    tfecWidth: 103.3,
    industry: 23.12,
    smes: 13.07,
    total: 47.96,
  },
  {
    country: "Malaysia",
    flag: "🇲🇾",
    tfec: 90036,
    tfecWidth: 90.03,
    industry: 23.32,
    smes: 13.87,
    total: 22.34,
  },
  {
    country: "Philippines",
    flag: "🇵🇭",
    tfec: 57675,
    tfecWidth: 57.67,
    industry: 14.93,
    smes: 10.77,
    total: 18.6,
  },
  {
    country: "Thailand (Sub)",
    flag: "🇹🇭",
    tfec: 21694,
    tfecWidth: 21.69,
    industry: 19.35,
    smes: 10.77,
    total: 12.62,
  },
];

export default function SectionBarChart({ isActive }: { isActive: boolean }) {
  const [data, setData] = useState<any[]>([]);
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        // We use the rawData directly now
        const formattedData = rawData.map((item) => ({
          ...item,
          tfecLabel: item.tfec.toLocaleString(),
          industryLabel: item.industry.toString(),
          smesLabel: item.smes.toString(),
        }));
        setData(formattedData);
        setAnimationKey((prev) => prev + 1);
      }, 200);
    } else {
      setData([]);
    }
  }, [isActive]);

  // Custom label to render percentage inside the bar
  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (width < 10) return null; // Show labels even for very small segments

    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={width < 35 ? "8px" : "10px"}
        fontWeight="bold"
      >
        {value}
      </text>
    );
  };

  const renderTotalLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    return (
      <text
        x={x + width + 10}
        y={y + height / 2}
        fill="#333"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize="12px"
        fontWeight="bold"
      >
        {value}
      </text>
    );
  };

  return (
    <div className="w-full h-full flex flex-col justify-center bg-[#f9f9f9] p-4 overflow-hidden">
      <div className="w-full h-[60vh] min-h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            key={animationKey}
            margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              stroke="#e0e0e0"
            />
            <XAxis type="number" hide />
            <YAxis
              dataKey="country"
              type="category"
              width={40}
              tick={({ x, y, payload }) => (
                <text x={x} y={y} dy={4} textAnchor="end" fontSize="22px">
                  {data[payload.index]?.flag}
                </text>
              )}
            />
            <Tooltip />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="square"
              wrapperStyle={{ fontSize: "10px", paddingBottom: "10px" }}
            />

            {/* TFEC Section */}
            <Bar
              dataKey="tfecWidth"
              name="TFEC"
              stackId="a"
              fill="#001529"
              barSize={25}
            >
              <LabelList dataKey="tfecLabel" content={renderCustomLabel} />
            </Bar>

            {/* Industry Section */}
            <Bar dataKey="industry" name="Industry" stackId="a" fill="#1e7e4a">
              <LabelList dataKey="industryLabel" content={renderCustomLabel} />
            </Bar>

            {/* SMEs Section */}
            <Bar dataKey="smes" name="SMEs" stackId="a" fill="#54667a">
              <LabelList dataKey="smesLabel" content={renderCustomLabel} />
              <LabelList dataKey="total" content={renderTotalLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
